// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type { ApiErrorPayloadCommon } from '@imprivata-cloud/common';
import type { CodingContext } from '@imprivata-cloud/data-privacy-js';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import type {
  EnrollIdentityCredentialRequest,
  EnrollIdentityCredentialResponse,
} from '../../models/digitalId';
import { usePlatform } from '../../context/platform/hooks';
import { apiClient } from '../client';

const enrollIdentityCredentialRequest = async (
  request: EnrollIdentityCredentialRequest,
  codingContext: CodingContext | undefined,
): Promise<EnrollIdentityCredentialResponse> => {
  return apiClient.post<EnrollIdentityCredentialResponse>(
    'enroll/identity-credential',
    () => request,
    codingContext,
  );
};

export const useEnrollIdentityCredentialMutation = (options: {
  onSuccess: (patientId: string) => void;
}) => {
  const { codingContext } = usePlatform();
  const [apiError, setApiError] = useState<ApiErrorPayloadCommon | null>(null);
  const mutation = useMutation({
    mutationFn: (request: EnrollIdentityCredentialRequest) =>
      enrollIdentityCredentialRequest(request, codingContext),
    onSuccess: (response) => options.onSuccess(response.id),
    onError: (error) => setApiError(error as ApiErrorPayloadCommon),
  });
  return {
    enrollIdentityCredential: mutation.mutate,
    isLoading: mutation.isPending,
    apiError,
    resetApiError: () => setApiError(null),
  };
};
